// ==================== Utilities ====================

import React from "react";
import { hasPermissionForRole } from "@/config/permission/permissions";
import { APP_NAVIGATION } from "@/routers/app-navigation";
import { ERoutesPathname } from "@/routers/config";
import withTsTmcPatientListGuard from "@/routers/guards/withTsTmcPatientListGuard";
import type { NavigationItem } from "@/routers/types";
import withPermissionGuard from "../config/permission/withPermissionGuard";

/**
 * Get sidebar menu configuration
 */

let __sidebarConfigCache: NavigationItem[] | null = null;

/**
 * Clear sidebar config cache
 * Call this when user role changes to force re-evaluation of permissions
 */
export const clearSidebarConfigCache = () => {
  __sidebarConfigCache = null;
};

export const getSidebarConfig = () => {
  if (__sidebarConfigCache) {
    return __sidebarConfigCache;
  }

  const filterSidebarItems = (items: NavigationItem[]): NavigationItem[] => {
    return items
      .filter(
        (item) => item.showInSidebar !== false && hasPermissionForRole(item.permissionRoles || []),
      )
      .map((item) => ({
        ...item,
        children: item.children ? filterSidebarItems(item.children) : undefined,
      }))
      .sort((a, b) => (a.order || 999) - (b.order || 999));
  };

  __sidebarConfigCache = filterSidebarItems(APP_NAVIGATION);
  return __sidebarConfigCache;
};

/**
 * Get flattened route configuration
 */
let __routeConfigCache: NavigationItem[] | null = null;
export const getRouteConfig = () => {
  if (__routeConfigCache) return __routeConfigCache;
  const extractRoutes = (items: NavigationItem[]): NavigationItem[] => {
    const routes: NavigationItem[] = [];

    items.forEach((item) => {
      routes.push(item);
      if (item.children) {
        routes.push(...extractRoutes(item.children));
      }
    });

    return routes;
  };

  __routeConfigCache = extractRoutes(APP_NAVIGATION);
  return __routeConfigCache;
};

/**
 * Find navigation item by path
 */
export const findNavigationItem = (path: string): NavigationItem | null => {
  const normalizePath = (p: string) => (p === "/" ? "/" : p.replace(/\/+$/u, ""));
  const searchInItems = (items: NavigationItem[], targetPath: string): NavigationItem | null => {
    const normalizedTarget = normalizePath(targetPath);
    for (const item of items) {
      // Exact match (normalized)
      if (normalizePath(item.path) === normalizedTarget) {
        return item;
      }

      // Parameterized match (e.g., /patient/edit/:id matches /patient/edit/123)
      const pathPattern = normalizePath(item.path).replace(/:[\w]+/g, "[^/]+");
      const regex = new RegExp(`^${pathPattern}$`);
      if (regex.test(normalizedTarget)) {
        return item;
      }

      // Recursively search child items
      if (item.children) {
        const found = searchInItems(item.children, normalizedTarget);
        if (found) return found;
      }
    }
    return null;
  };

  return searchInItems(APP_NAVIGATION, path);
};

/**
 * Generate breadcrumb trail
 */
export const generateBreadcrumbs = (
  currentPath: string,
  context?: { id?: string | number; name?: string } | undefined,
): Array<{ title: string; path?: string }> => {
  const breadcrumbs: Array<{ title: string; path?: string }> = [];
  const normalize = (p: string) => (p === "/" ? "/" : p.replace(/\/+$/, ""));
  const normalizedCurrentPath = normalize(currentPath);
  const chargeCodeRoot = normalize(ERoutesPathname.chargeCodeManagement);

  if (
    normalizedCurrentPath === chargeCodeRoot ||
    normalizedCurrentPath.startsWith(`${chargeCodeRoot}/`)
  ) {
    return [{ title: "Charge Code Management", path: ERoutesPathname.chargeCodeManagement }];
  }

  const extractParams = (template: string, actualPath: string): Record<string, string> => {
    const tmpl = normalize(template);
    const actual = normalize(actualPath);
    const paramNames = Array.from(tmpl.matchAll(/:([\w]+)/g)).map((m) => m[1]);
    if (paramNames.length === 0) return {};
    const pattern = tmpl.replace(/:[\w]+/g, "([^/]+)");
    const match = actual.match(new RegExp(`^${pattern}$`));
    if (!match) return {};
    const values = match.slice(1);
    const params: Record<string, string> = {};
    paramNames.forEach((name, i) => {
      params[name] = values[i];
    });
    return params;
  };

  // Prefix matching strategy: find the longest path chain with parent prefixes
  const isPrefixMatch = (template: string, actual: string): boolean => {
    const pattern = normalize(template).replace(/:[\w]+/g, "[^/]+");
    const re = new RegExp(`^${pattern}($|/)`);
    return re.test(normalize(actual));
  };

  const buildBestTrail = (
    items: NavigationItem[],
    targetPath: string,
    trail: Array<{ title: string; path?: string }>,
  ): Array<{ title: string; path?: string }> => {
    let best = trail;
    for (const item of items) {
      if (!isPrefixMatch(item.path, targetPath)) continue;
      // Avoid duplication when parent and child share the exact same path (e.g., invoice and "/invoice")
      const lastPath = trail.length > 0 ? normalize(trail[trail.length - 1].path || "") : "";
      const currentPath = normalize(item.path);
      if (lastPath === currentPath) continue;

      const nextTrail = [...trail, { title: item.breadcrumb || item.title, path: item.path }];
      const deeper = item.children
        ? buildBestTrail(item.children, targetPath, nextTrail)
        : nextTrail;
      if (deeper.length > best.length) best = deeper;
    }
    return best;
  };

  let trail = buildBestTrail(APP_NAVIGATION, normalizedCurrentPath, []);

  // If the last level is an exact match, apply dynamic title rules for edit/detail routes
  const finalizeDynamicTitle = (crumbs: Array<{ title: string; path?: string }>) => {
    if (crumbs.length === 0) return crumbs;
    const last = crumbs[crumbs.length - 1];
    if (!last.path) return crumbs;
    const lastPattern = normalize(last.path).replace(/:[\w]+/g, "[^/]+");
    const isExact = new RegExp(`^${lastPattern}$`).test(normalizedCurrentPath);
    if (!isExact) return crumbs;

    // Locate the original NavigationItem to read pageTitle etc. (simplified: find again by path)
    const findNavByPath = (items: NavigationItem[], p: string): NavigationItem | null => {
      for (const it of items) {
        if (normalize(it.path) === normalize(p)) return it;
        if (it.children) {
          const found = findNavByPath(it.children, p);
          if (found) return found;
        }
      }
      return null;
    };
    const item = findNavByPath(APP_NAVIGATION, last.path);
    if (!item) return crumbs;

    const paramsFromPath = extractParams(item.path, normalizedCurrentPath);
    const isEditRoute = /\/edit\/:/.test(item.path);
    const isIdDetailRoute = item.path === "/invoice/credit-note/:id";
    const isDischargeAdviceEditRoute = item.path === ERoutesPathname.dischargeAdviceEdit;
    const isGenericIdDetailRoute =
      /\/:id$/.test(item.path) && !isEditRoute && !/\/(create|finalize)\//.test(item.path);
    const idFromContextOrPath = context?.id != null ? String(context.id) : paramsFromPath.id;
    let dynamicTitle: string | undefined;
    if (isEditRoute) {
      dynamicTitle =
        context?.name ??
        paramsFromPath.name ??
        (isDischargeAdviceEditRoute ? undefined : idFromContextOrPath);
    } else if (isIdDetailRoute) {
      dynamicTitle = context?.name ?? paramsFromPath.name ?? idFromContextOrPath;
    } else if (isGenericIdDetailRoute) {
      dynamicTitle = context?.name ?? paramsFromPath.name ?? idFromContextOrPath;
    }
    if (!dynamicTitle && typeof item.pageTitle === "function" && !isDischargeAdviceEditRoute) {
      const safeContext: Record<string, string> = {};
      if (context?.name != null) safeContext.name = String(context.name);
      if (context?.id != null) safeContext.id = String(context.id);
      dynamicTitle = item.pageTitle({ ...(paramsFromPath || {}), ...safeContext });
    }
    if (dynamicTitle) {
      const updated = [...crumbs];
      updated[updated.length - 1] = { ...last, title: dynamicTitle };
      return updated;
    }
    return crumbs;
  };

  trail = finalizeDynamicTitle(trail);

  // For action routes like "/.../edit/:id" or "/.../finalize/:id",
  // remove the sibling detail breadcrumb ("/.../:id") to avoid duplicated levels like
  // Inventory > Dispensing > Dispensing Detail > Edit Dispensing.
  const removeRedundantDetailForActionRoutes = (
    crumbs: Array<{ title: string; path?: string }>,
  ): Array<{ title: string; path?: string }> => {
    if (crumbs.length < 2) return crumbs;
    const last = crumbs[crumbs.length - 1];
    if (!last.path) return crumbs;
    const lastPath = normalize(last.path);
    const actionMatch = lastPath.match(/^(.*)\/(edit|finalize)\/:id$/);
    if (!actionMatch) return crumbs;
    const base = actionMatch[1]; // Base path after removing action and :id, e.g., /inventory/dispensing
    const detailPath = normalize(`${base}/:id`);

    const idxToRemove = crumbs.findIndex((c) => normalize(c.path || "") === detailPath);
    if (idxToRemove === -1) return crumbs;

    const updated = crumbs.filter((_, i) => i !== idxToRemove);
    return updated;
  };

  trail = removeRedundantDetailForActionRoutes(trail);

  // Fallback: build breadcrumbs by segment-level matching to ensure levels like Inventory > Suppliers show up
  const buildFallback = (): Array<{ title: string; path?: string }> => {
    const segments = normalizedCurrentPath.split("/").filter(Boolean);
    const paths: string[] = [];
    segments.forEach((_seg, idx) => {
      paths.push(`/${segments.slice(0, idx + 1).join("/")}`);
    });

    const getItemTitle = (item: NavigationItem | null): string =>
      item ? item.breadcrumb || item.title : "";

    const findItem = (p: string): NavigationItem | null => {
      // Use the existing logic in this file for matching
      const pathPattern = (tmpl: string) => tmpl.replace(/:[\w]+/g, "[^/]+");
      const search = (items: NavigationItem[]): NavigationItem | null => {
        for (const it of items) {
          if (normalize(it.path) === normalize(p)) return it;
          // Only allow dynamic param matching when p is the current full path,
          // to avoid partial matches like "/inventory/dispensing/edit" matching "/inventory/dispensing/:id"
          if (normalize(p) === normalizedCurrentPath) {
            const re = new RegExp(`^${pathPattern(normalize(it.path))}$`);
            if (re.test(normalize(p))) return it;
          }
          if (it.children) {
            const found = search(it.children);
            if (found) return found;
          }
        }
        return null;
      };
      return search(APP_NAVIGATION);
    };

    const fallbackCrumbs: Array<{ title: string; path?: string }> = [];
    paths.forEach((p) => {
      const item = findItem(p);
      if (item) {
        fallbackCrumbs.push({ title: getItemTitle(item), path: item.path });
      }
    });
    return fallbackCrumbs;
  };

  let fallback = buildFallback();
  // Apply dynamic title and redundancy cleanup to fallback as well for consistency
  fallback = finalizeDynamicTitle(fallback);
  fallback = removeRedundantDetailForActionRoutes(fallback);

  const withOverrides = (): Array<{ title: string; path?: string }> => {
    const normalized = normalizedCurrentPath;

    const getItemTitle = (item: NavigationItem | null): string =>
      item ? item.breadcrumb || item.title : "";

    const findItem = (p: string): NavigationItem | null => {
      const pathPattern = (tmpl: string) => tmpl.replace(/:[\w]+/g, "[^/]+");
      const search = (items: NavigationItem[]): NavigationItem | null => {
        for (const it of items) {
          if (normalize(it.path) === normalize(p)) return it;
          const re = new RegExp(`^${pathPattern(normalize(it.path))}$`);
          if (re.test(normalize(p))) return it;
          if (it.children) {
            const found = search(it.children);
            if (found) return found;
          }
        }
        return null;
      };
      return search(APP_NAVIGATION);
    };

    const result: Array<{ title: string; path?: string }> = [];

    const selfItem = findItem(normalized);
    if (selfItem) result.push({ title: getItemTitle(selfItem), path: selfItem.path });
    return result;
  };

  const overridden = withOverrides();
  const best = [trail, breadcrumbs, fallback, overridden].reduce(
    (a, b) => (b.length > a.length ? b : a),
    [] as Array<{ title: string; path?: string }>,
  );
  return best;
};

/**
 * Generate React Router route configuration with hierarchical permission checking
 *
 * Key Features:
 * - If parent route has no permission, all child routes are also excluded
 * - Permission inheritance: children inherit parent's access status
 * - Non-hook permission checking for use outside React components
 */
export const generateRouterConfig = () => {
  const routes: Array<{ path: string; element: React.ReactElement }> = [];
  const seenPaths = new Set<string>();

  /**
   * Recursively extract routes with hierarchical permission checking
   * @param items - Navigation items to process
   * @param parentHasAccess - Whether the parent route has access (default: true for root)
   */
  const extractRoutes = (items: NavigationItem[], parentHasAccess: boolean = true): void => {
    for (const item of items) {
      // Skip invalid paths and routes already manually defined
      const conditions = [
        item.component,
        item.path !== "#",
        item.path !== ERoutesPathname.home,
        item.path !== ERoutesPathname.showcase,
        item.path !== ERoutesPathname.login,
        item.path !== ERoutesPathname.testPage,
        item.path !== ERoutesPathname.additionalInformation,
      ];

      if (conditions.every((condition) => !!condition)) {
        // Check if current item has permission
        // const currentItemHasAccess =
        //   item.permissionRoles && item.permissionRoles.length > 0
        //     ? hasPermissionForRole(item.permissionRoles)
        //     : true;

        // Hierarchical permission: If parent doesn't have access, child also doesn't have access
        // const finalAccess = parentHasAccess && currentItemHasAccess;

        if (!seenPaths.has(item.path) && parentHasAccess) {
          seenPaths.add(item.path);
          let GuardedComponent = item.component as React.ComponentType;

          if (item.path === ERoutesPathname.patient) {
            GuardedComponent = withTsTmcPatientListGuard(GuardedComponent);
          }

          const ElementWithGuard =
            item.permissionRoles && item.permissionRoles.length > 0
              ? withPermissionGuard(GuardedComponent, item.permissionRoles)
              : GuardedComponent;

          routes.push({
            path: item.path,
            element: React.createElement(ElementWithGuard),
          });
        }
      }

      if (item.children) {
        extractRoutes(item.children, true);
      }
    }
  };

  extractRoutes(APP_NAVIGATION);
  return routes;
};
